import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  NotificationsApi,
  type UpdateUserNotificationPreferenceItemDto,
  type UserNotificationPreferenceDto,
} from '@up2stay/up2promo-api-client';
import { toast } from 'sonner';

import { useDictionary } from '@/core/providers/dictionary-provider';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

export const userPrefsKey = (workspaceId: string | undefined) =>
  ['notifications', 'user-preferences', workspaceId] as const;

export function useUserNotificationPreferences() {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const queryClient = useQueryClient();
  const dict = useDictionary();

  const query = useQuery({
    queryKey: userPrefsKey(workspaceId),
    enabled: Boolean(workspaceId),
    queryFn: async (): Promise<UserNotificationPreferenceDto[]> => {
      const response = await NotificationsApi.getUserPreferences({
        query: { workspaceId: workspaceId! },
      } as never);
      return (response.data ?? []) as UserNotificationPreferenceDto[];
    },
  });

  const mutation = useMutation({
    mutationFn: async (
      preferences: UpdateUserNotificationPreferenceItemDto[],
    ) => {
      const response = await NotificationsApi.updateUserPreferences({
        body: {
          workspaceId: workspaceId ?? null,
          preferences,
        },
      });
      return response.data ?? [];
    },
    onSuccess: (data) => {
      queryClient.setQueryData(userPrefsKey(workspaceId), data);
    },
    onError: () => {
      toast.error(dict.settings.notifications.saveError);
    },
  });

  return { ...query, savePreferences: mutation.mutateAsync };
}
